import i18n from "@config/i18n";
import type { ValidationMessageParams, ValidationPresentationField } from "../../types";

/**
 * Legacy group/section copies reused by the wizard. They interpolate context the
 * joint-creation mutation does not return, so every placeholder is emptied and the
 * remaining sentence is trimmed.
 */
const LEGACY_MESSAGE_KEY_BY_REASON: Record<string, string> = {
  CLASSROOM_CAPACITY_EXCEEDED: "bundle.create-group.errors.CLASSROOM_CAPACITY_EXCEEDED.message",
  ENROLLMENTS_NOT_COVERED: "bundle.create-group.errors.ENROLLMENTS_NOT_COVERED.message",
  GROUP_CODE_ALREADY_TAKEN: "bundle.create-group.errors.GROUP_CODE_ALREADY_TAKEN.message",
  GROUP_VACANCIES_EXTERNAL_VALIDATION:
    "bundle.create-group.errors.GROUP_VACANCIES_EXTERNAL_VALIDATION.message",
  LOWER_THAN_MIN: "bundle.create-group.errors.LOWER_THAN_MIN.message",
  SUBJECT_NOT_FOUND: "bundle.create-group.errors.SUBJECT_NOT_FOUND.message",
};

const EMPTY_LEGACY_PLACEHOLDERS = {
  capacityMs: "",
  enrrollmentMs: "",
  groupMs: "",
  sectionMs: "",
  sessionMs: "",
  standardMs: "",
};

const FIELD_BY_REASON: Record<string, ValidationPresentationField> = {
  CLASSROOM_CAPACITY_EXCEEDED: "group-vacancies",
  COULD_NOT_CREATE_GROUP: "group-code",
  ENROLLMENTS_NOT_COVERED: "group-vacancies",
  EXEEDS_MAX: "group-vacancies",
  GROUP_CODE_ALREADY_TAKEN: "group-code",
  GROUP_CODE_REQUIRED_OR_INVALID: "group-code",
  GROUP_VACANCIES_EXTERNAL_VALIDATION: "group-vacancies",
  GROUP_VACANCIES_REQUIRED: "group-vacancies",
  GROUP_VACANCIES_SOURCE_INVALID: "group-vacancies",
  LOWER_THAN_MIN: "group-vacancies",
  NUMERIC_GROUP_CODE_VALIDATION: "group-code",
  ONLY_NUMBERS: "group-code",
  ONLY_NUMBERS_WITHOUT_ZERO: "group-code",
  RETAKER_CAPACITY_EXCEEDS_TOTAL: "group-vacancies",
};

const wizardKey = (reason: string) => `package-wizard.validation.${reason}`;

/**
 * Resolves the localized copy for one backend validation reason.
 *
 * Wizard copies win over the legacy group ones, and any unmapped reason falls back
 * to a generic message carrying the raw code so a validation is never shown blank.
 */
export const resolveValidationMessage = (
  reason: string,
  params: ValidationMessageParams = {},
): string => {
  const capacityKey = `${wizardKey(reason)}-with-capacity`;
  if (typeof params.capacity === "number" && i18n.exists(capacityKey)) {
    return i18n.t(capacityKey, { capacity: params.capacity });
  }

  if (i18n.exists(wizardKey(reason))) return i18n.t(wizardKey(reason));

  const legacyKey = LEGACY_MESSAGE_KEY_BY_REASON[reason];
  if (legacyKey) return i18n.t(legacyKey, EMPTY_LEGACY_PLACEHOLDERS).trim();

  return i18n.t("package-wizard.validation.unknown", { reason });
};

/**
 * Group input a validation message belongs to. Only subjects that submit an initial
 * group own those inputs, so every other subject keeps the message at subject level.
 */
export const resolveValidationField = (
  reason: string,
  hasInitialGroup: boolean,
): ValidationPresentationField | undefined =>
  hasInitialGroup ? FIELD_BY_REASON[reason] : undefined;
